import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import LocaleLink from "@/components/LocaleLink";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface FooterLink {
  label: string;
  to: string;
}

interface FooterColumn {
  title: string;
  /** Flat link list (rendered when no `groups` provided) */
  links?: FooterLink[];
  /** Grouped link list with sub-headings inside the column */
  groups?: { heading: string; links: FooterLink[] }[];
}

const MegaFooter = () => {
  const { t } = useTranslation();
  // The columns are an accordion below lg and a plain grid at lg and up.
  // Keyed on the breakpoint so `open` re-applies when the viewport crosses it;
  // between crossings the disclosure is uncontrolled, so a reader's own toggle
  // sticks.
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const columns: FooterColumn[] = [
    {
      title: t("megaFooter.platform"),
      links: [
        { label: t("megaFooter.features"), to: "/features" },
        { label: t("megaFooter.aiGrcPlatform"), to: "/ai-grc-platform" },
        { label: t("megaFooter.automatedControlMapping"), to: "/automated-control-mapping" },
        { label: t("megaFooter.controlApplicabilityWizard"), to: "/control-applicability-wizard" },
        { label: t("megaFooter.automatedGapAnalysis"), to: "/automated-gap-analysis" },
        { label: t("megaFooter.continuousMonitoring"), to: "/integrations/continuous-monitoring" },
        { label: t("megaFooter.riskManagement"), to: "/risk-management" },
        { label: t("megaFooter.thirdPartyRisk"), to: "/third-party-risk" },
        { label: t("megaFooter.questionnaireAutomation"), to: "/questionnaire-automation" },
        { label: t("megaFooter.audit"), to: "/audit" },
        { label: t("megaFooter.integrations"), to: "/integrations" },
        { label: t("megaFooter.pricing"), to: "/pricing" },
      ],
    },
    {
      title: t("megaFooter.solutions"),
      groups: [
        {
          heading: t("megaFooter.byIndustry"),
          links: [
            { label: t("megaFooter.saas"), to: "/for/saas" },
            { label: t("megaFooter.fintech"), to: "/for/fintech" },
            { label: t("megaFooter.financialServices"), to: "/for/financial-services" },
            { label: t("megaFooter.healthcare"), to: "/for/healthcare" },
            { label: t("megaFooter.publicSector"), to: "/for/public-sector" },
            { label: t("megaFooter.cybersecurity"), to: "/for/cybersecurity" },
            { label: t("megaFooter.hrWorkforce"), to: "/for/hr-workforce" },
          ],
        },
        {
          heading: t("megaFooter.byRegion"),
          links: [
            { label: t("megaFooter.africanFintech"), to: "/for/africa" },
            { label: t("megaFooter.mena"), to: "/for/mena" },
            { label: t("megaFooter.eu"), to: "/for/eu" },
            { label: t("megaFooter.apac"), to: "/for/apac" },
            { label: t("megaFooter.americas"), to: "/for/americas" },
          ],
        },
      ],
    },
    {
      title: t("megaFooter.resources"),
      links: [
        { label: t("megaFooter.insights"), to: "/insights" },
        { label: t("megaMenu.docs"), to: "https://docs.csquaregrc.com/" },
        { label: t("megaFooter.templates"), to: "/templates" },
        { label: t("megaFooter.allDownloads"), to: "/resources" },
        { label: t("megaFooter.allChecklists"), to: "/resources?category=checklist" },
        { label: t("megaFooter.roiCalculator"), to: "/roi-calculator" },
        { label: t("megaFooter.readiness"), to: "/readiness" },
        { label: t("megaFooter.ripeAssessment"), to: "/ripe-assessment" },
        { label: t("megaFooter.complianceAutomationGuide"), to: "/insights/guide/compliance-automation-roi" },
        { label: t("megaFooter.hikersMindsetWhitepaper"), to: "/hikers-mindset" },
        { label: t("megaFooter.useCases"), to: "/use-cases" },
        { label: t("megaFooter.whatsNew"), to: "/whats-new" },
      ],
    },
    {
      title: t("megaFooter.company"),
      links: [
        { label: t("megaFooter.about"), to: "/about" },
        { label: t("megaFooter.ourApproach"), to: "/our-approach" },
        { label: t("megaFooter.whyCsquare"), to: "/why-csquare" },
        { label: t("megaFooter.regulatedEnterprises"), to: "/regulated-enterprises" },
        { label: t("megaFooter.managedServices"), to: "/managed-services" },
        { label: t("megaFooter.partners"), to: "/partners" },
        { label: t("megaFooter.careers"), to: "/careers" },
        { label: t("megaFooter.careerTransition"), to: "/career-transition" },
        { label: t("megaFooter.contact"), to: "/contact" },
        { label: t("megaFooter.support"), to: "/support" },
        { label: t("megaFooter.trustCenter"), to: "/trust" },
      ],
    },
  ];

  // The long tail. Every one of these URLs used to be a column of its own,
  // stacked open on mobile. Closed by default at every breakpoint, and the
  // markup is still in the page, so the internal links are unchanged for
  // crawlers while a reader on a phone scrolls past one line instead of forty.
  const directory: { heading: string; links: FooterLink[] }[] = [
    {
      heading: t("megaFooter.frameworks"),
      links: [
        { label: "SOC 2", to: "/templates?template=soc-2&source=footer-fw" },
        { label: "ISO 27001", to: "/templates?template=iso-27001&source=footer-fw" },
        { label: "GDPR", to: "/templates?template=gdpr&source=footer-fw" },
        { label: "HIPAA", to: "/templates?template=hipaa&source=footer-fw" },
        { label: "NIST CSF", to: "/templates?template=nist-csf&source=footer-fw" },
        { label: "PCI DSS", to: "/templates?template=pci-dss&source=footer-fw" },
        { label: "DORA", to: "/templates?template=dora&source=footer-fw" },
        { label: "NIS2", to: "/templates?template=nis2&source=footer-fw" },
        { label: "CCPA", to: "/templates?template=ccpa&source=footer-fw" },
        { label: "ISO 42001", to: "/templates?template=iso-42001&source=footer-fw" },
        { label: "SOX", to: "/templates?template=sox&source=footer-fw" },
      ],
    },
    {
      heading: t("megaFooter.checklists"),
      links: [
        { label: t("megaFooter.soc2Checklist"), to: "/insights/checklist/soc2-compliance-checklist" },
        { label: t("megaFooter.iso27001Checklist"), to: "/insights/checklist/iso27001-compliance-checklist" },
        { label: t("megaFooter.gdprChecklist"), to: "/insights/checklist/gdpr-compliance-checklist" },
        { label: t("megaFooter.hipaaChecklist"), to: "/insights/checklist/hipaa-compliance-checklist" },
        { label: t("megaFooter.pciChecklist"), to: "/insights/checklist/pci-dss-4-compliance-checklist" },
        { label: t("megaFooter.nistCsfChecklist"), to: "/insights/checklist/nist-csf-compliance-checklist" },
        { label: t("megaFooter.nist800171Checklist"), to: "/insights/checklist/nist-800-171-compliance-checklist" },
        { label: t("megaFooter.ccpaChecklist"), to: "/insights/checklist/ccpa-cpra-compliance-checklist" },
        { label: t("megaFooter.iso27701Checklist"), to: "/insights/checklist/iso27701-compliance-checklist" },
        { label: t("megaFooter.cisChecklist"), to: "/insights/checklist/cis-controls-v8-compliance-checklist" },
        { label: t("megaFooter.soxChecklist"), to: "/insights/checklist/sox-compliance-checklist" },
        { label: t("megaFooter.zeroToAuditChecklist"), to: "/insights/checklist/zero-to-audit-ready-checklist" },
      ],
    },
    {
      heading: t("megaFooter.bySize"),
      links: [
        { label: t("megaFooter.smallBusiness"), to: "/for/small-business" },
        { label: t("megaFooter.midMarket"), to: "/for/mid-market" },
        { label: t("megaFooter.enterprise"), to: "/contact-sales?plan=enterprise" },
      ],
    },
  ];

  const renderLinkList = (links: FooterLink[]) => (
    <ul className="mt-3 space-y-2 text-sm text-brand-ink-foreground/80">
      {links.map((link) => (
        <li key={link.to + link.label}>
          {link.to.startsWith("https://") ? (
            <a href={link.to} className="hover:text-brand-ink-foreground transition-colors">{link.label}</a>
          ) : (
            <LocaleLink to={link.to} className="hover:text-brand-ink-foreground transition-colors">
              {link.label}
            </LocaleLink>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <div className="grid grid-cols-1 items-start gap-2 lg:grid-cols-4 lg:gap-8">
        {columns.map((col) => (
          <details
            key={`${col.title}-${isDesktop}`}
            open={isDesktop}
            className="group border-b border-brand-ink-foreground/10 pb-3 lg:border-0 lg:pb-0"
          >
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between lg:min-h-0 lg:cursor-default lg:pointer-events-none">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-ink-foreground md:text-sm">
                {col.title}
              </h3>
              <ChevronDown className="h-4 w-4 text-brand-ink-foreground/70 transition-transform group-open:rotate-180 lg:hidden" />
            </summary>
            {col.groups ? (
              <div className="mt-3 space-y-4">
                {col.groups.map((grp) => (
                  <div key={grp.heading}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-ink-foreground/60">
                      {grp.heading}
                    </div>
                    {renderLinkList(grp.links)}
                  </div>
                ))}
              </div>
            ) : col.links ? (
              renderLinkList(col.links)
            ) : null}
          </details>
        ))}
      </div>

      <details className="group mt-8 border-t border-brand-ink-foreground/10 pt-4">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-ink-foreground/60">
            {t("megaFooter.directory")}
          </span>
          <ChevronDown className="h-4 w-4 text-brand-ink-foreground/60 transition-transform group-open:rotate-180" />
        </summary>
        <div className="mt-4 space-y-4">
          {directory.map((grp) => (
            <div key={grp.heading} className="flex flex-col gap-1.5 sm:flex-row sm:gap-4">
              <div className="shrink-0 pt-px text-[11px] font-semibold uppercase tracking-wider text-brand-ink-foreground/50 sm:w-32">
                {grp.heading}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-brand-ink-foreground/70">
                {grp.links.map((link, i) => (
                  <span key={link.to + link.label} className="flex items-center">
                    <LocaleLink to={link.to} className="hover:text-brand-ink-foreground transition-colors">
                      {link.label}
                    </LocaleLink>
                    {i < grp.links.length - 1 && (
                      <span className="ml-3 select-none text-brand-ink-foreground/25" aria-hidden>
                        ·
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

export default MegaFooter;
